import { useMutation, useQueryClient } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortBuilderDefinitionLegacy, CohortVersion } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { invalidateCohort } from './invalidateCohort';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';

export interface SaveCohortVersionInput {
  projectId: string;
  /** Aka cohortId — the cohort profile id under which the version lives. */
  profileId: string;
  /**
   * When provided, PUTs an update to the existing version (DRAFT/UNSAVED
   * path). When absent, POSTs a new version (ERROR/PROCESSED path).
   */
  versionId?: string;
  dslVersion?: string;
  sqlValue?: string;
  jsonValue?: CohortBuilderDefinitionLegacy;
}

/**
 * Canonical TanStack mutation for persisting the cohort builder definition.
 * Replaces the RTK `usePostCohortVersionMutation` / `usePutCohortVersionMutation`
 * pair — picks POST vs PUT internally based on whether `versionId` is set.
 *
 * Optimistic updates: callers should patch the Zustand cohort-definition
 * store themselves in their submit handler; the mutation does not write to
 * Zustand directly. On success the cohort profile cache (cohortQueryKeys.all)
 * is invalidated so the latest version + status round-trip from the server.
 */
export const useSaveCohortVersionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ObjectResponse<CohortVersion>, Error, SaveCohortVersionInput>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: async ({ projectId, profileId, versionId, dslVersion, sqlValue, jsonValue }) => {
      const isUpdate = !!versionId;
      const url = isUpdate
        ? `/${COHORTS_PATH}/${projectId}/cohort-profile/${profileId}/cohort-version/${versionId}`
        : `/${COHORTS_PATH}/${projectId}/cohort-profile/${profileId}/cohort-version`;
      const body = isUpdate ? { sqlValue, jsonValue } : { dslVersion, sqlValue, jsonValue };
      return authFetch<ObjectResponse<CohortVersion>>(url, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_data, { projectId, profileId, versionId }) => {
      void invalidateCohort(queryClient, { cohortId: profileId, projectId, versionId });
    },
  });
};
